import React, { SetStateAction, useContext, useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import {
  TablePopoverMenu,
  TablePopoverMenuItemType,
} from 'components/TablePopoverMenu/TablePopoverMenu'
import { PathNames } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useIsActiveWallet } from 'hooks/useActiveWallets'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ActionIds, WalletType, WalletTypes } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { PayActionBeneficiaryTypes } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { useMappedRoles } from '../../../../hooks/useMappedRoles'
import { WalletAction } from '../Modal/WalletModal'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../Pay/atoms/atoms'

interface Props {
  setShowWalletModal?: React.Dispatch<React.SetStateAction<boolean>>
  setWalletModalAction: React.Dispatch<SetStateAction<WalletAction>>
  setIsSheetOpen?: React.Dispatch<SetStateAction<boolean>>
  type: WalletTypes
  wallet: WalletType
}

const Submenu = ({
  setShowWalletModal,
  setWalletModalAction,
  setIsSheetOpen,
  type,
  wallet,
}: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const permissions = useMappedRoles()
  const { viewOnly } = useProfileContext()
  const { enableUsLaunchRestrictions, enableConvertSheet, enabledUnifiedFiatSendFlow } =
    useFeatureFlags()
  const [, setWalletAction] = useContext(WalletActionContext)

  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setFiatFormAtom = useSetAtom(fiatSendFormAtom)
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)
  const customerModel = useAtomValue(customerModelAtom)
  const isEmbeddedCustomer = customerModel === CustomerModels.EMBEDDED

  const isNetworkWallet = wallet?.supportsInternalBvnkNetworkTransfers
  const canEdit = permissions?.wallets?.canEdit
  const canTrade = permissions?.wallets?.canTrade
  const canEditOrTrade = canEdit || canTrade
  const canCreatePayOut = permissions?.payments?.canCreatePayOut
  const canPay = isNetworkWallet ? permissions?.bvnkNetwork?.canTrade : canEdit || canTrade

  const isSelectedWalletActive = useIsActiveWallet(wallet)
  const isFiat = type?.toUpperCase() === WalletTypes.FIAT
  const hasBalance = wallet?.balance > 0

  const sharedSubMenuConfig: TablePopoverMenuItemType[] = [
    {
      iconName: 'ClockCounterClockwiseIcon',
      label: t('wallets.transactionHistory'),
      onClick: () =>
        navigate(`${PathNames.WALLETS}/wallet-details/${wallet?.id}?tab=recent-activity`),
    },
  ]

  const walletDetails: TablePopoverMenuItemType = {
    iconName: 'InfoCircleIcon',
    label: t('wallets.walletDetails'),
    onClick: () => {
      navigate(`${PathNames.WALLETS}/wallet-details/${wallet?.id}?tab=recent-activity`)
    },
  }

  if (canEdit && isSelectedWalletActive && !viewOnly) {
    sharedSubMenuConfig.push({
      iconName: 'EditIcon',
      label: t('wallets.editWalletName'),
      onClick: () => handleOpenWalletEditSheet(),
    })
  }

  const handleSetWalletModalAction = (action: WalletAction) => {
    const { key } = action
    setShowWalletModal?.(true)
    setWalletModalAction(action)
    track.Amp.track(AmplitudeEvent.WALLET_POPOVER_MENU, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      flow: key,
      currency: wallet?.currency?.code?.toLowerCase(),
      walletId: wallet?.id,
    })
  }

  const handleOpenConvertSheet = () => {
    setWalletAction({
      actionId: ActionIds?.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenWalletEditSheet = () => {
    setWalletAction({
      actionId: ActionIds?.WALLET_EDIT,
      header: t('wallets.editWalletName'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenMoveSheet = () => {
    setWalletAction({
      actionId: ActionIds.MOVE,
      header: t('customers.moveFunds'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWalletType: WalletTransferType.MAIN,
        sourceWallet: wallet,
        isEmbeddedCustomerModel: isEmbeddedCustomer,
      },
    })
    setIsSheetOpen(true)
  }

  const { supportsThirdParty, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
    wallet

  const isLegacyPayEnabled = supportsThirdParty && canPay && isSelectedWalletActive && !viewOnly
  const isLegacyWithdrawalEnabled =
    supportsWithdrawals && canEditOrTrade && isSelectedWalletActive && !viewOnly
  const isUnifiedPayoutEnabled =
    enabledUnifiedFiatSendFlow && (isLegacyPayEnabled || isLegacyWithdrawalEnabled)

  const handleOpenFiatDepositSheet = (walletId: number) => {
    if (walletId) {
      setWalletAction({
        actionId: ActionIds?.FIAT_DEPOSIT,
        header: t('deposit'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        sourceWalletId: wallet?.id,
      })
    }
    setIsSheetOpen(true)
  }

  const cleanupUnifiedPayoutsFlow = () => {
    setBeneficiaryAtom(null)
    setFiatFormAtom(null)
    setSourceWalletAtom(null)
    setWalletAction(null)
    setIsSheetOpen(false)
  }

  const handleOpenSendFiatSheet = () => {
    if (wallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_SEND,
        header: t('wallets.fiatSend.beneficiaryStepHeader'),
        onClose: cleanupUnifiedPayoutsFlow,
        sourceWalletReference: wallet?.lsid,
      })

      setIsSheetOpen(true)
    }
  }

  const fiatMenuOptions = useMemo(() => {
    const menuItems: TablePopoverMenuItemType[] = [...sharedSubMenuConfig]

    const isWalletDetailsEnabled = supportsDeposits && canEditOrTrade && isSelectedWalletActive
    const isLegacyPayButtonEnabled = !enabledUnifiedFiatSendFlow && isLegacyPayEnabled
    const isLegacyWithdrawButtonEnabled = !enabledUnifiedFiatSendFlow && isLegacyWithdrawalEnabled
    const isSendCryptoButtonEnabled =
      hasBalance && isSelectedWalletActive && canCreatePayOut && !viewOnly
    const isDepositButtonEnabled =
      supportsDeposits && canEditOrTrade && isSelectedWalletActive && !viewOnly
    const isConvertButtonEnabled =
      canEditOrTrade && isSelectedWalletActive && !viewOnly && !enableUsLaunchRestrictions
    const isMoveFundsButtonEnabled =
      isSelectedWalletActive && supportsInternalTransfers && !viewOnly

    return [
      ...menuItems,
      ...(isWalletDetailsEnabled ? [walletDetails] : []),
      ...(isLegacyPayButtonEnabled
        ? [
            {
              iconName: 'PaymentIcon',
              label: t('transactionAction.pay'),
              onClick: () => {
                handleSetWalletModalAction({
                  key: 'pay',
                  data: wallet,
                  metadata: {
                    pay: { beneficiaryPaymentType: PayActionBeneficiaryTypes.FIAT },
                  },
                })
              },
            } as TablePopoverMenuItemType,
          ]
        : []),
      ...(isSendCryptoButtonEnabled
        ? [
            {
              iconName: 'ArrowUpRightIcon',
              label: t('payments.sendCrypto'),
              onClick: () => {
                setWalletAction({
                  actionId: ActionIds?.SEND,
                  header: t('wallets.send.contactStepHeader'),
                  onClose: () => {
                    setIsSheetOpen(false)
                  },
                  meta: {
                    sourceWallet: wallet,
                  },
                })
                setIsSheetOpen(true)
              },
            } as TablePopoverMenuItemType,
          ]
        : []),
      ...(isUnifiedPayoutEnabled
        ? [
            {
              iconName: 'PaymentIcon',
              label: t('wallets.fiatSend.sendFiat'),
              onClick: () => {
                handleOpenSendFiatSheet()
              },
            } as TablePopoverMenuItemType,
          ]
        : []),
      ...(isLegacyWithdrawButtonEnabled
        ? [
            {
              iconName: 'WithdrawIcon',
              label: t('transactionAction.withdraw'),
              onClick: () => {
                handleSetWalletModalAction({ key: 'withdraw', data: wallet })
              },
            } as TablePopoverMenuItemType,
          ]
        : []),
      ...(isDepositButtonEnabled
        ? [
            {
              iconName: 'DepositIcon',
              label: t('transactionAction.deposit'),
              onClick: () => {
                handleOpenFiatDepositSheet(wallet?.id)
              },
            } as TablePopoverMenuItemType,
          ]
        : []),
      ...(isConvertButtonEnabled
        ? [
            {
              iconName: 'ConvertIcon',
              label: t('transactionAction.convert'),
              onClick: () => {
                enableConvertSheet
                  ? handleOpenConvertSheet()
                  : handleSetWalletModalAction({ key: 'convert', data: wallet })
              },
            } as TablePopoverMenuItemType,
          ]
        : []),
      ...(isMoveFundsButtonEnabled
        ? [
            {
              iconName: 'ConvertIcon',
              label: t('customers.move'),
              onClick: handleOpenMoveSheet,
            } as TablePopoverMenuItemType,
          ]
        : []),
    ]
  }, [wallet, enableUsLaunchRestrictions])

  const CryptoWalletMenuItems = (): TablePopoverMenuItemType[] => {
    const menuItems: TablePopoverMenuItemType[] = [...sharedSubMenuConfig]

    if (canEditOrTrade && isSelectedWalletActive) {
      menuItems.push(walletDetails)
    }

    if (canEditOrTrade && isSelectedWalletActive && hasBalance && canCreatePayOut && !viewOnly) {
      menuItems.push({
        iconName: 'ArrowUpRightIcon',
        label: t('transactionAction.send'),
        onClick: () => {
          setWalletAction({
            actionId: ActionIds?.SEND,
            header: t('wallets.send.contactStepHeader'),
            onClose: () => {
              setIsSheetOpen(false)
            },
            meta: {
              sourceWallet: wallet,
            },
          })
          setIsSheetOpen(true)
        },
      })
    }

    if (canEditOrTrade && isSelectedWalletActive) {
      menuItems.push({
        iconName: 'ArrowDownLeftIcon',
        label: t('transactionAction.receive'),
        onClick: () => {
          navigate(`${PathNames.WALLETS}/wallet-details/${wallet?.id}?tab=receive-crypto`)
        },
      })
    }
    if (canEditOrTrade && isSelectedWalletActive && !enableUsLaunchRestrictions) {
      menuItems.push({
        iconName: 'ConvertIcon',
        label: t('transactionAction.convert'),
        onClick: () => {
          enableConvertSheet
            ? handleOpenConvertSheet()
            : handleSetWalletModalAction({ key: 'convert', data: wallet })
        },
      })
    }

    return menuItems
  }

  const popOverMenuKey = useMemo(() => {
    if (isFiat) {
      // Ensure popover re-renders if capability values change
      const actionsKeys = [
        ...(supportsThirdParty ? ['p'] : []),
        ...(supportsDeposits ? ['d'] : []),
        ...(supportsWithdrawals ? ['w'] : []),
        ...(supportsInternalTransfers ? ['m'] : []),
      ]?.toString()

      return `fiat-popover-${wallet?.lsid}-${actionsKeys}`
    }

    return `crypto-popover-${wallet?.lsid}`
  }, [isFiat, wallet])

  return (
    <TablePopoverMenu
      key={popOverMenuKey}
      stopPropagation
      hasPadding
      testid={isFiat ? 'fiatContextPopoverMenu' : 'cryptoContextPopoverMenu'}
      menuItems={isFiat ? fiatMenuOptions : CryptoWalletMenuItems()}
    />
  )
}

export default Submenu
